// Offline sales queue — localStorage-backed, replayed via create_sale when
// the connection returns. Scope is deliberately narrow: cash, card and ნისია
// sales (ნისია since 2026-09-26 — create_sale ADDS the debt and checks no
// limit, so a late replay cannot fork a balance); no bonus (its balance is
// checked at replay), no fiscal (the receipt flow needs the live pipeline),
// no shift open/close, holds, or returns offline.
//
// Each entry stores the COMPLETE create_sale RPC payload plus the org id it
// was created under, so a queue can never replay into a different tenant if
// someone logs into another shop on the same browser.

export type OfflineSaleEntry = {
  qid: string            // client-generated queue id (also the temp sale id in UI)
  org_id: string         // tenant guard — replay only under the same org
  created_at: string     // when the sale physically happened (offline)
  payload: Record<string, unknown>  // full named-params object for supabase.rpc('create_sale', …)
  total: number          // for the banner/UI summary
  failed?: string        // last replay error message (kept in queue for retry)
}

/** The queue's localStorage key. */
export const OFFLINE_QUEUE_KEY = 'offline_sales_queue_v1'
const KEY = OFFLINE_QUEUE_KEY

// What the queue is allowed to drop: nothing. A sale it cannot replay (a
// queue that no longer parses, an element that is not a sale) is moved aside
// under `${KEY}_unreadable_<time>`, whole, before anything is written over it
// — the support desk can read it there (review R08, 2026-10-04).
const isEntry = (e: unknown): e is OfflineSaleEntry =>
  !!e && typeof e === 'object'
  && typeof (e as OfflineSaleEntry).qid === 'string'
  && !!(e as OfflineSaleEntry).payload && typeof (e as OfflineSaleEntry).payload === 'object'

/** The stored text, the sales in it, and whether that is ALL of it. */
function readQueue(): { raw: string | null; entries: OfflineSaleEntry[]; whole: boolean } {
  const raw = localStorage.getItem(KEY)
  if (raw == null) return { raw, entries: [], whole: true }
  try {
    const arr: unknown = JSON.parse(raw)
    if (!Array.isArray(arr)) return { raw, entries: [], whole: false }
    const entries = arr.filter(isEntry)
    return { raw, entries, whole: entries.length === arr.length }
  } catch {
    return { raw, entries: [], whole: false }
  }
}

export function loadOfflineQueue(): OfflineSaleEntry[] {
  if (typeof window === 'undefined') return []
  try {
    return readQueue().entries
  } catch {
    return []
  }
}

/**
 * Writes the queue and says whether it did. FALSE when the storage is full or
 * blocked, or when the stored queue holds something unreadable that could not
 * be moved aside first — and a sale whose write returned false is saved
 * NOWHERE, so the caller must say so. It used to log to the console and let
 * the till announce „saved" (review R08, 2026-10-04).
 */
export function saveOfflineQueue(queue: OfflineSaleEntry[]): boolean {
  if (typeof window === 'undefined') return false
  try {
    const cur = readQueue()
    if (!cur.whole && cur.raw != null) {
      localStorage.setItem(`${KEY}_unreadable_${Date.now()}`, cur.raw)
      console.error('offline queue held unreadable data — kept aside, not overwritten')
    }
    localStorage.setItem(KEY, JSON.stringify(queue))
    return true
  } catch (e) {
    console.error('offline queue save failed', e)
    return false
  }
}

export function enqueueOfflineSale(entry: OfflineSaleEntry): boolean {
  return saveOfflineQueue([...loadOfflineQueue(), entry])
}

export function removeFromOfflineQueue(qid: string): boolean {
  return saveOfflineQueue(loadOfflineQueue().filter(e => e.qid !== qid))
}

export function markOfflineSaleFailed(qid: string, message: string): boolean {
  return saveOfflineQueue(loadOfflineQueue().map(e => (e.qid === qid ? { ...e, failed: message } : e)))
}

/** 140 — an offline sale's replay: never fiscal, never bounced on stock, and
 *  carrying WHEN it happened, which the server keeps when it is believable. */
export function offlinePayload(rpcPayload: Record<string, unknown>, soldAt: string): Record<string, unknown> {
  return { ...rpcPayload, p_is_fiscal: false, p_allow_oversell: true, p_sold_at: soldAt }
}

/** 140 — the live refresh's filter: a replayed sale is stamped when it
 *  happened, maybe an hour back, so „new" also means „newly arrived". */
export function liveSalesFilter(since: string): string {
  return `created_at.gte."${since}",received_at.gte."${since}"`
}

/** 140 review #1 — loss control's sales: recent by creation OR arrival, AND a
 *  return or a discount. One `or=` param (an `and` inside it), so nothing
 *  depends on how PostgREST combines two of them. */
export function lossSalesFilter(since: string): string {
  return `and(or(${liveSalesFilter(since)}),or(type.eq.return,discount.gt.0))`
}

// ---- A press retried after „not saved" ----
// When a sale could be saved neither on the server nor in the queue, the cart
// stays and the cashier presses „გაყიდვა" again. If the first request did
// reach the server (its answer lost with the network), a NEW id would make it
// a second sale; the same id makes create_sale answer the one it has. So the
// unsaved attempt's id is offered back to the next press of the SAME cart —
// for ten minutes, so a different customer's identical basket later is not
// mistaken for it.

export const ATTEMPT_TTL_MS = 10 * 60 * 1000

export type UnsavedAttempt = { qid: string; key: string; at: number }

/** What makes two presses the same sale: the money and the lines. */
export function saleAttemptKey(
  sale: { total: number; payment_method?: string; discount?: number | null; paid?: number | null; paid_cash?: number | null; paid_card?: number | null; customer_id?: string | null },
  items: Array<{ product_id?: string | null; quantity: number; unit_price: number; total_price: number }>,
  /** 1.57 — the prepaid it spends: another card, or another amount, is another attempt (Codex #5) */
  prepaid?: { id: string; amount: number } | null,
): string {
  return JSON.stringify([
    sale.total, sale.payment_method ?? null, sale.discount ?? 0, sale.paid ?? null,
    sale.paid_cash ?? null, sale.paid_card ?? null, sale.customer_id ?? null,
    items.map(i => [i.product_id ?? null, i.quantity, i.unit_price, i.total_price]),
    // appended only when there is one, so every other cart keeps the key it had
    ...(prepaid ? [[prepaid.id, prepaid.amount]] : []),
  ])
}

/**
 * 1.57 — a sale that spends a bonus or a prepaid is never queued offline: the
 * balance is the server's and is checked at the sale. When its reply is lost
 * after the request left (it may have committed), the till keeps the SAME
 * client ref for the next press of the same cart, so the server answers the
 * first sale instead of spending the card twice (Codex #6).
 */
export function onlineOnlySale(
  sale: { bonus_redeemed?: number | null },
  prepaid?: { id: string; amount: number } | null,
  /** 1.58 — a line at the customer's group price: create_sale accepts it online only (179) */
  groupPriced: boolean = false,
): boolean {
  return (sale.bonus_redeemed ?? 0) > 0 || !!prepaid || groupPriced
}

/**
 * 1.47 (Codex's audit): what makes two presses the same RETURN — the opening
 * of the return dialog they were pressed in, the sale it comes back from and
 * the lines. A return whose answer was lost after the commit, pressed again,
 * used to be a second return (two of two units back for one handed over);
 * with the same id create_sale answers the first. The dialog stays open on a
 * failure, so that retry happens in the same opening; a later, really new
 * return of an identical unit is made from a new opening (Codex's review) and
 * gets its own id.
 */
export function returnAttemptKey(
  originalId: string,
  items: Array<{ product_id?: string | null; quantity: number; unit_price: number; total_price: number; serials?: string[] | null }>,
  session: string | null = null,
): string {
  // 128: the units named are part of the return — another IMEI is another return
  return JSON.stringify(['return', session, originalId,
    items.map(i => [i.product_id ?? null, i.quantity, i.unit_price, i.total_price, i.serials ?? null])])
}

/**
 * 1.47 (Codex's review): a debt payment whose answer was lost on the network —
 * did it land? pay_customer and pay_supplier both lower the balance by exactly
 * the amount, so the balance read again tells: moved by the amount = booked.
 * Anything else (unchanged, or moved by another device's sum) is „not known
 * to have landed", and the failure is said as before.
 */
export function paymentLanded(before: number | null | undefined, after: number | null | undefined, amount: number): boolean {
  if (before == null || after == null) return false
  return Math.abs(before - amount - after) < 0.005
}

/** The unsaved attempt's id, when this press is that attempt again; else null. */
export function reuseAttempt(prev: UnsavedAttempt | null, key: string, now: number): string | null {
  return prev && prev.key === key && now - prev.at <= ATTEMPT_TTL_MS ? prev.qid : null
}

/**
 * An auth call that failed on the NETWORK, not on the answer (2026-09-26).
 * auth-js does not retry, so this is the fastest honest „the server is
 * unreachable" the app gets — the browser's own flag stays true when the wifi
 * is up and the internet behind it is not.
 */
export function isAuthNetworkError(err: unknown): boolean {
  const e = err as { name?: string; status?: number; message?: string } | null
  if (!e) return false
  return e.name === 'AuthRetryableFetchError' || e.status === 0 || /failed to fetch|network/i.test(e.message ?? '')
}

/** True when the browser reports a network connection. */
export const isBrowserOnline = () =>
  typeof navigator === 'undefined' ? true : navigator.onLine

/** Heuristic: did this supabase error come from the network being down (vs the server rejecting)? */
export function isNetworkError(err: unknown): boolean {
  const msg = String((err as { message?: string })?.message ?? err ?? '').toLowerCase()
  return (
    msg.includes('failed to fetch') ||
    msg.includes('fetch failed') ||
    msg.includes('networkerror') ||
    msg.includes('network request failed') ||
    msg.includes('load failed') // Safari's fetch failure wording
  )
}
